export const whiteLabel = {
  seo: {
    title: "Bespire White Label – Launch Your Own Branded Software Platform",
    description: "White-label SaaS platform for agencies, consultants, and operators. Launch your own branded software business with booking, payments, websites, CRM, and AI tools.",
    keywords: "white label SaaS, white label platform, branded client platform, SaaS reseller, partner platform, vertical SaaS",
  },
  navbar: {
    overview: "Overview",
    whoItsFor: "Who it's for",
    features: "Features",
    models: "Models",
    howItWorks: "How it works",
    faq: "FAQ",
    bookCall: "Book a call",
  },
  hero: {
    badge: "White Label Partner Program",
    title: "Launch your own branded software platform without building from scratch.",
    subtitle: "Bespire White Label helps agencies, operators, and partners launch their own SaaS offer with their own brand, faster, leaner, and with less risk.",
    primaryCta: "Book a strategy call",
    secondaryCta: "See how it works",
    trustItems: [
      "European platform",
      "AI-native foundation",
      "Modular & scalable",
    ],
  },
  whoItsFor: {
    label: "Who it's for",
    title: "Built for operators who think in platforms.",
    subtitle: "Whether you run an agency, a franchise, or a consulting business — Bespire White Label gives you a technology foundation to sell under your brand.",
    cards: [
      {
        title: "Agencies",
        pain: "Clients expect more than campaigns. They want tools.",
        outcome: "Offer a branded client platform alongside your services.",
        icon: "building2",
      },
      {
        title: "Consultants",
        pain: "Your methodology lives in slides and spreadsheets.",
        outcome: "Package your process into a branded software experience.",
        icon: "briefcase",
      },
      {
        title: "Franchise operators",
        pain: "Each location runs different tools and processes.",
        outcome: "Standardize operations under one branded platform.",
        icon: "network",
      },
      {
        title: "Education networks",
        pain: "Scaling training programs requires scattered tools.",
        outcome: "Deliver courses, bookings, and content under your brand.",
        icon: "graduationCap",
      },
      {
        title: "Coaching businesses",
        pain: "Growing beyond 1-on-1 means losing the personal touch.",
        outcome: "Scale your coaching with branded digital journeys.",
        icon: "heart",
      },
      {
        title: "Wellness & fitness networks",
        pain: "Studios and trainers each use different software.",
        outcome: "Unify your network with one branded platform.",
        icon: "dumbbell",
      },
      {
        title: "Software resellers",
        pain: "Reselling generic tools means competing on price alone.",
        outcome: "Own the brand, own the relationship, own the margin.",
        icon: "shoppingBag",
      },
    ],
  },
  opportunity: {
    label: "The opportunity",
    title: "Why white-label is the smartest path to SaaS revenue.",
    subtitle: "Stop trading time for money. Start building a recurring revenue business on a proven technology foundation.",
    items: [
      {
        title: "Recurring revenue",
        description: "Build predictable MRR by packaging software under your brand and selling it to your clients.",
        icon: "trendingUp",
      },
      {
        title: "Faster market entry",
        description: "Skip years of development. Go to market in weeks, not quarters.",
        icon: "zap",
      },
      {
        title: "No full-stack build required",
        description: "Get booking, payments, websites, CRM, and automation without hiring an engineering team.",
        icon: "code",
      },
      {
        title: "Own the brand & relationship",
        description: "Your clients see your brand, your domain, your identity. Bespire stays invisible.",
        icon: "palette",
      },
      {
        title: "Niche positioning",
        description: "Configure the platform for your vertical — fitness, wellness, coaching, consulting, education, or any niche.",
        icon: "target",
      },
      {
        title: "From services to SaaS",
        description: "Expand your service business into a scalable software business with recurring revenue.",
        icon: "arrowUpRight",
      },
    ],
  },
  features: {
    label: "What you can sell",
    title: "Everything your clients need. Under your brand.",
    subtitle: "Bespire provides the modular technology stack. You define the packaging, pricing, and positioning.",
    items: [
      { title: "Website builder", description: "Branded web presence for every client", icon: "globe" },
      { title: "Booking & scheduling", description: "Calendar, availability, and appointment management", icon: "calendar" },
      { title: "Lead capture funnels", description: "Landing pages and conversion flows", icon: "filter" },
      { title: "Online payments", description: "Secure payments, invoicing, and checkout", icon: "creditCard" },
      { title: "Memberships", description: "Subscriptions, access levels, and recurring billing", icon: "users" },
      { title: "Digital products", description: "Courses, content, downloads, and offers", icon: "package" },
      { title: "Client journeys", description: "Onboarding flows and guided experiences", icon: "map" },
      { title: "CRM workflows", description: "Client management, tags, and segments", icon: "database" },
      { title: "Automation", description: "Reminders, follow-ups, and triggered actions", icon: "bot" },
      { title: "AI growth tools", description: "AI-assisted content, outreach, and optimization", icon: "sparkles" },
      { title: "Analytics", description: "Reporting, dashboards, and business intelligence", icon: "barChart" },
      { title: "Vertical configs", description: "Industry-specific setups and templates", icon: "settings" },
    ],
  },
  comparison: {
    label: "Why not build it yourself?",
    title: "Three paths. One clear winner.",
    subtitle: "Compare the options and see why Bespire White Label is the optimal path for operators who want speed, ownership, and scalability.",
    headers: {
      criteria: "Criteria",
      buildFromScratch: "Build from scratch",
      resellTools: "Resell disconnected tools",
      bespire: "Bespire White Label",
    },
    rows: [
      { criteria: "Time to market", scratch: "12-24 months", tools: "1-3 months", bespire: "4-8 weeks" },
      { criteria: "Development cost", scratch: "€200K-€1M+", tools: "Low", bespire: "Predictable & lean" },
      { criteria: "Brand ownership", scratch: "Full", tools: "Limited", bespire: "Full" },
      { criteria: "Feature depth", scratch: "Custom", tools: "Fragmented", bespire: "Complete & modular" },
      { criteria: "Maintenance burden", scratch: "High", tools: "Medium", bespire: "Managed" },
      { criteria: "Scalability", scratch: "Depends", tools: "Limited", bespire: "Built-in" },
      { criteria: "AI capabilities", scratch: "Build yourself", tools: "None", bespire: "Native" },
      { criteria: "Customer relationship", scratch: "Yours", tools: "Shared", bespire: "Yours" },
      { criteria: "Risk level", scratch: "Very high", tools: "Medium", bespire: "Low" },
    ],
  },
  howItWorks: {
    label: "How it works",
    title: "From first call to live platform in weeks.",
    subtitle: "A structured, transparent process designed for serious operators.",
    steps: [
      {
        number: "01",
        title: "Discovery call",
        description: "We learn about your business, market, and goals. You learn about Bespire's capabilities.",
      },
      {
        number: "02",
        title: "Qualification & fit",
        description: "We assess mutual fit — your use case, target market, and partnership model alignment.",
      },
      {
        number: "03",
        title: "Model definition",
        description: "We define the white-label model, scope, pricing structure, and commercial terms together.",
      },
      {
        number: "04",
        title: "Brand & packaging setup",
        description: "Your brand, your domain, your positioning. We configure the platform to match your identity.",
      },
      {
        number: "05",
        title: "Pilot launch",
        description: "Go live with your first clients or locations. Test, learn, and refine the offer.",
      },
      {
        number: "06",
        title: "Scale",
        description: "Expand to new clients, verticals, or geographies with a proven, branded platform.",
      },
    ],
  },
  models: {
    label: "Partnership models",
    title: "Choose the model that fits your business.",
    subtitle: "Bespire White Label supports multiple partnership structures. Pick the one that aligns with your go-to-market strategy.",
    items: [
      {
        title: "Reseller",
        description: "Buy licenses at partner rates, sell under your brand at your pricing. Simple, scalable, margin-driven.",
        bestFor: "Agencies, software distributors, service firms",
        icon: "repeat",
      },
      {
        title: "Managed service",
        description: "Bundle the platform with your consulting, implementation, or managed services. Deliver a complete solution.",
        bestFor: "Consultants, implementation partners, managed service providers",
        icon: "settings",
      },
      {
        title: "Vertical solution",
        description: "Configure the platform for a specific industry vertical. Own the niche positioning and market expertise.",
        bestFor: "Niche operators, vertical SaaS builders, industry specialists",
        icon: "layers",
      },
      {
        title: "Network / Multi-brand",
        description: "Deploy the platform across a network of locations, franchises, or sub-brands. Centralized control, localized brands.",
        bestFor: "Franchise networks, multi-location operators, brand groups",
        icon: "network",
      },
      {
        title: "Strategic implementation",
        description: "Deep integration with your existing ecosystem. Co-develop features and go-to-market strategy.",
        bestFor: "Enterprise partners, strategic alliances, platform ecosystems",
        icon: "handshake",
      },
    ],
  },
  useCases: {
    label: "Real-world scenarios",
    title: "How partners use Bespire White Label.",
    subtitle: "Practical examples of how different businesses build recurring revenue on the Bespire foundation.",
    cases: [
      {
        title: "Agency growth platform",
        description: "A marketing agency launches its own branded client-growth platform — combining websites, booking, funnels, and CRM under their brand. Clients pay monthly, the agency earns recurring revenue.",
        industry: "Marketing & Digital",
      },
      {
        title: "Wellness network",
        description: "A wellness franchise offers branded software to its studios and independent instructors. Booking, payments, and client management — all under the network's brand.",
        industry: "Wellness & Fitness",
      },
      {
        title: "Vertical SaaS play",
        description: "A consultant creates a niche SaaS offer for nutrition professionals — combining scheduling, content delivery, and client journeys into one branded product.",
        industry: "Health & Consulting",
      },
      {
        title: "Franchise standardization",
        description: "A franchise network standardizes digital operations across 50+ locations. Each location gets its own branded sub-platform with centralized reporting.",
        industry: "Franchise & Operations",
      },
      {
        title: "Creator operating system",
        description: "An e-learning business launches a branded operating system for its expert network — enabling course delivery, bookings, payments, and community under one roof.",
        industry: "Education & Creators",
      },
    ],
  },
  whyBespire: {
    label: "Why Bespire",
    title: "A platform built for partners, not just end users.",
    subtitle: "Bespire is designed from the ground up to support white-label deployment, niche verticalization, and partner-driven growth.",
    points: [
      { title: "European market DNA", description: "Built with European sensibility — multilingual, multi-currency, privacy-aware, and culturally flexible." },
      { title: "Flexible approach", description: "No rigid templates. Configure the platform to match your market, your niche, and your business logic." },
      { title: "Premium product direction", description: "A product your clients will be proud to use. Modern, fast, and beautifully designed." },
      { title: "AI-native foundation", description: "AI is not an add-on. It's built into the platform — from content creation to client engagement to automation." },
      { title: "Partner-first thinking", description: "We succeed when our partners succeed. Our roadmap, pricing, and support reflect this." },
      { title: "Modular scalability", description: "Start with one module. Scale to the full stack. Add features as your clients grow." },
      { title: "Speed + strategic support", description: "Fast launches with hands-on support. Not a self-serve platform — a strategic partnership." },
      { title: "Built for serious operators", description: "Not a DIY tool. A professional platform for partners who want to build real businesses." },
    ],
  },
  faq: {
    label: "Frequently asked questions",
    title: "Everything you need to know.",
    items: [
      {
        question: "What exactly is white-label here?",
        answer: "White-label means you take the Bespire platform, apply your own brand (logo, colors, domain, positioning), and sell it to your clients as your own product. Bespire remains invisible to your end users.",
      },
      {
        question: "Can we use our own brand and domain?",
        answer: "Absolutely. Your brand, your domain, your identity. Your clients will never see the Bespire name unless you want them to.",
      },
      {
        question: "Can the solution be adapted to our niche?",
        answer: "Yes. The platform is modular and configurable. You can activate, deactivate, and customize features to match your specific vertical — whether that's fitness, consulting, education, or any other niche.",
      },
      {
        question: "Is this better than building custom software?",
        answer: "For most operators, yes. Building from scratch takes 12-24 months and significant investment. With Bespire White Label, you can launch in weeks with a proven, maintained, and evolving platform.",
      },
      {
        question: "Who owns the customer relationship?",
        answer: "You do. Your clients are your clients. You set the pricing, manage the relationship, and own the revenue. Bespire is your technology partner, not your competitor.",
      },
      {
        question: "Can we define our own pricing?",
        answer: "Yes. You set your own pricing for your clients. Bespire provides partner-level pricing to you, and you define your margin and packaging strategy.",
      },
      {
        question: "Do you support multi-location or multi-client setups?",
        answer: "Yes. The platform is designed for multi-tenant deployment. Whether you have 5 clients or 500 locations, the architecture supports it.",
      },
      {
        question: "Can we start with a pilot?",
        answer: "Absolutely. We recommend starting with a focused pilot — a few clients or one vertical — to validate the model before scaling.",
      },
      {
        question: "What type of partners are the best fit?",
        answer: "Partners who already have a client base, industry expertise, and a desire to add recurring software revenue to their business. Agencies, consultants, franchise operators, and niche specialists are ideal.",
      },
      {
        question: "How long does launch usually take?",
        answer: "From signed agreement to live platform: typically 4-8 weeks, depending on customization scope and brand setup complexity.",
      },
      {
        question: "What is included in the partnership?",
        answer: "Platform access, white-label configuration, onboarding support, partner pricing, technical documentation, and ongoing product updates. Strategic partners also receive dedicated account management.",
      },
      {
        question: "Is this suitable for agencies only?",
        answer: "Not at all. While agencies are a strong fit, we work with consultants, franchise networks, education platforms, wellness networks, and any operator who wants to launch branded software.",
      },
    ],
  },
  booking: {
    label: "Book a call",
    title: "Let's explore your white-label model.",
    subtitle: "Book a strategy call and we'll map out whether Bespire White Label is a fit for your market, offer, and business model.",
    calendarPlaceholder: "Why get in touch?",
    calendarNote: "A short conversation is all it takes to find out how Bespire White Label fits your business model.",
    calendarCta: "Request a callback",
    calendarTrust: "No commitment · 30 minutes · Personalized",
    calendarReasons: [
      { title: "Tailored proposal", description: "No generic demo — we build a strategy based on your market and target audience." },
      { title: "Concrete roadmap", description: "We'll show you the exact steps to launch your own branded platform within weeks." },
      { title: "ROI calculation", description: "We'll calculate the revenue potential the white-label model represents for you." },
      { title: "Risk-free", description: "No commitment, no hidden costs — just a valuable 30-minute expert consultation." },
      { title: "Partner materials", description: "After the call you'll receive a detailed partner deck and a custom pricing proposal." },
    ],
    form: {
      title: "Tell us about your business",
      name: "Full name",
      namePlaceholder: "Your name",
      company: "Company",
      companyPlaceholder: "Your company name",
      email: "Email",
      emailPlaceholder: "you@company.com",
      website: "Website",
      websitePlaceholder: "https://yourcompany.com",
      businessType: "Business type",
      businessTypePlaceholder: "Select your business type",
      businessTypes: {
        agency: "Agency",
        consultant: "Consultant / Advisor",
        franchise: "Franchise / Multi-location",
        education: "Education / Training",
        coaching: "Coaching / Mentoring",
        wellness: "Wellness / Fitness",
        reseller: "Software reseller",
        other: "Other",
      },
      targetNiche: "Target niche",
      targetNichePlaceholder: "e.g., Fitness studios, beauty salons, coaching...",
      clientCount: "Approximate client count",
      clientCountPlaceholder: "Select range",
      clientCounts: {
        range1: "1-10",
        range2: "11-50",
        range3: "51-200",
        range4: "200+",
      },
      model: "What white-label model interests you?",
      modelPlaceholder: "Select a model",
      models: {
        reseller: "Reseller",
        managed: "Managed service",
        vertical: "Vertical solution",
        network: "Network / Multi-brand",
        strategic: "Strategic implementation",
        notSure: "Not sure yet",
      },
      timeline: "Timeline",
      timelinePlaceholder: "Select timeline",
      timelines: {
        asap: "As soon as possible",
        oneToThree: "1-3 months",
        threeToSix: "3-6 months",
        exploring: "Just exploring",
      },
      message: "Message",
      messagePlaceholder: "Tell us about your vision, market, or any specific questions...",
      submit: "Submit & book a call",
      submitting: "Submitting...",
      successTitle: "Thank you!",
      successMessage: "We've received your inquiry. Our team will reach out within 24 hours to schedule your strategy call.",
    },
  },
  ctaBanner: {
    title: "Ready to launch your own branded platform?",
    subtitle: "Join the partner program and start building recurring SaaS revenue.",
    cta: "Book a strategy call",
    secondaryCta: "Request partner deck",
  },
  finalCta: {
    title: "Explore your white-label growth model with Bespire.",
    subtitle: "The technology is ready. The model is proven. The question is: are you ready to launch?",
    primaryCta: "Book a call",
    secondaryCta: "Request partner information",
  },
  footer: {
    description: "The white-label platform for agencies, operators, and partners who want to launch branded software businesses.",
    product: "Product",
    overview: "Platform overview",
    features: "Features",
    models: "Partner models",
    pricing: "Pricing",
    company: "Company",
    about: "About Bespire",
    partners: "Partner program",
    careers: "Careers",
    contact: "Contact",
    legal: "Legal",
    privacy: "Privacy policy",
    terms: "Terms of service",
    cookies: "Cookie policy",
    imprint: "Imprint",
    location: "Budapest, Hungary",
    allRightsReserved: "All rights reserved.",
  },
};
